// src/services/api/user.js
import { apiRequest } from "@/services/api/apiClient.js";
import {getCorp, updateCorp} from "@/services/api/corporation.js";
import {getUser} from "@/services/api/auth.js";
import {PermissionManagementEntity} from "@/entities/PermissionManagementEntity.js";

const entity = new PermissionManagementEntity();

const getAppMenuName = () => {
    return window.location.pathname.includes('/rhdigital')
        ? 'hliberty.RHDigital'
        : 'hLiberty.Admin';
};

const getAppMenuUrl = () => `/appmenu/${getAppMenuName()}`;

export async function getPermissionsManagement({ page = 1, limit = 10, ...filters }) {
    const corp = await getCorp();
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/workgroup?corp_id=${corp?.id ?? ''}`,
        params: { page, limit, ...filters },
    });
}

export async function getMenu({ page = 1, limit = 10, corpId = '', idWorkgroup = '', ...filters }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `${getAppMenuUrl()}?workgroupId=${idWorkgroup}&corpId=${corpId}`,
        params: { },
    });
}

export async function getMePermissionsManagement() {
    try {
        const data = localStorage.getItem("permissions");
        return data ? JSON.parse(data) : {};
    } catch (error) {
        console.error("Error: ", error);
        return {};
    }
}

export async function setMePermissionsManagement({id, advancedCredentials = null}) {
    let permissions = "";
    let user = "";
    if (advancedCredentials) {
        user = getUser();
        permissions = user.fullUser.permission;
    } else {
        permissions = await apiRequest({
            client: window.API_AUTH,
            method: "get",
            url: `${getAppMenuUrl()}?userId=${id}`
        });
    }
    localStorage.setItem("permissions", JSON.stringify(permissions));
}

export async function getServicesByName({ name }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: "/service",
        params: { name },
    });
}

export function getFirstServiceId(response = {}) {
    return response?.data?.[0]?.id || null;
}

export async function addServiceToCorp({ corpId, serviceId }) {
    if (!corpId || !serviceId) {
        return Promise.reject(new Error('corpId and serviceId are required'));
    }

    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: `/corp/${corpId}/addService/${serviceId}`,
        body: undefined,
    });
}

export async function addPermissions({ idWorkgroup, corpId, actions }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "post",
        url: `${getAppMenuUrl()}/${idWorkgroup}`,
        body: { corpId, actions },
    });
}

export async function savePermissionManagement(permissionData) {
    const isUpdate = !!permissionData.id;

    permissionData = {
        ...permissionData,
        corp_id: permissionData.corp_id,
        users: permissionData.users ? permissionData.users.map(u => u.value) : []
    }

    let url = isUpdate
        ? entity.getUrl(entity.endpoints.get, { id: permissionData.id })
        : entity.endpoints.create;

    const response = await apiRequest({
        client: window.API_AUTH,
        method: isUpdate ? "put" : "post",
        url,
        body: permissionData,
    });

    if(permissionData.permissions && permissionData.permissions.length > 0) {
        const permissionsArray = Array.isArray(permissionData.permissions)
            ? permissionData.permissions
            : permissionData.permissions.split(",").map(v => v.trim());
        await addPermissions({idWorkgroup: response.id, corpId: permissionData.corp_id, actions: permissionsArray});
    }
    if (isUpdate) {
        const { data } = await getPermissionsManagementById({ id: permissionData.id});
        const currentUsers = Object.keys(data.users || {});

        const usersToAddIds = permissionData.users;
        const usersToRemoveIds = currentUsers.filter(currentId => !usersToAddIds.includes(currentId));
        for (const userId of usersToAddIds) {
            await addUsertoPermissionManagement({ idPermissionManagement: permissionData.id, idUser: userId });
        }
        for (const userId of usersToRemoveIds) {
            await removeUserFromPermissionManagement({ idPermissionManagement: permissionData.id, idUser: userId });
        }

        await updateCorp({business_name: data.corp.business_name, id: data.corp.id});
    }
    return response;
}

export const addUsertoPermissionManagement = async ({ idPermissionManagement, idUser }) => {
    try {
        return await apiRequest({
            client: window.API_AUTH,
            method: "post",
            url: `/workgroup/${idPermissionManagement}/addUser/${idUser}`,
        });
    } catch (error) {
        return Promise.reject(error);
    }
};

export const removeUserFromPermissionManagement = async ({ idPermissionManagement, idUser }) => {
    try {
        return await apiRequest({
            client: window.API_AUTH,
            method: "delete",
            url: `/workgroup/${idPermissionManagement}/remUser/${idUser}`,
        });
    } catch (error) {
        return Promise.reject(error);
    }
};

export async function getPermissionsManagementById({id}) {
    return apiRequest({
        client: window.API_AUTH,
        method: "get",
        url: `/workgroup/${id}`
    });
}

export async function deletePermissionsManagement({ id }) {
    return apiRequest({
        client: window.API_AUTH,
        method: "delete",
        url: `/workgroup/${id}`
    });
}

function simplifyActions(actionsFromApi = []) {
    const allowed = actionsFromApi
        .filter(action => action.allow)
        .map(action => action.title.toLowerCase());

    const actionsObject = Object.fromEntries(allowed.map(a => [a, a]));

    return actionsObject;
}

function findPermissionByRoute(permissions, targetPath, currentPath = "") {
    for (const item of permissions) {
        const fullPath = currentPath ? `${currentPath}/${item.title}` : item.title;

        if (fullPath.toLowerCase() === targetPath.toLowerCase()) {
            return item;
        }

        if (item.menu && item.menu.length) {
            const found = findPermissionByRoute(item.menu, targetPath, fullPath);
            if (found) return found;
        }
    }

    return null;
}

export async function getAvailableActionsForRoute(routeKey) {
    const permissions = await getMePermissionsManagement();

    if ((permissions?.admin?.[0] === "*" &&
        permissions?.allow?.[0] === "*") || routeKey.includes('QRCode')) {

        return {
            edit: "edit",
            view: "view",
            delete: "delete",
            create: "create",
            list: "list",
        }
    }
    if (!permissions) return {};

    const match = findPermissionByRoute(permissions, routeKey);
    if (!match?.menu) return {};

    const allowedActions = simplifyActions(match.menu);
    return allowedActions;
}
